import type {
	FieldFilter,
	FieldFilterOperator,
	Filter,
	FilterRelationalQuantifier,
	PrimitiveType,
	PrimitiveTypeName,
	ResolvedMonospaceCollection,
	ResolvedMonospaceSingleRelationField,
} from '#layers/data-model/public';
import type { DeepEntry } from './flatten-field-selection';
import { Temporal } from 'temporal-polyfill';
import { isZonedDateTime } from '#layers/base/public';
import {
	collapseOpenRanges,
	FilterOperator,
	getTypename,
	isFieldFilterOperator,
	isFilterRelationalQuantifier,
	isLogicalFilterAnd,
	isLogicalFilterOr,
	isPrimitiveTypeAnyDateTime,
	isUnaryFilterNot,
	resolveFieldParser,
} from '#layers/data-model/public';

interface ExtractedQuantifier {
	quantifier: FilterRelationalQuantifier;
	innerFilter: FieldFilter;
}

interface FilterOperandNormalizationContext {
	primitiveFieldTypes?: Record<string, PrimitiveType>;
	schemaCollection?: ResolvedMonospaceCollection;
	getPrimitiveFieldTypes?: (schemaCollection: ResolvedMonospaceCollection) => Record<string, PrimitiveType> | undefined;
}

/**
 * Converts canonical in-memory filter operands into backend wire values.
 *
 * Primitive field types come from RStore collection metadata. Schema collection metadata is only
 * used to follow relation filters into the opposite collection; unknown fields pass through.
 */
export function normalizeFilterOperandsForBackend(
	filter: Filter,
	context: FilterOperandNormalizationContext,
): Filter {
	if (isLogicalFilterAnd(filter)) {
		return {
			_and: filter._and.map((nestedFilter) => normalizeFilterOperandsForBackend(nestedFilter, context)),
		};
	}

	if (isLogicalFilterOr(filter)) {
		return {
			_or: filter._or.map((nestedFilter) => normalizeFilterOperandsForBackend(nestedFilter, context)),
		};
	}

	if (isUnaryFilterNot(filter)) {
		return {
			_not: normalizeFilterOperandsForBackend(filter._not, context),
		};
	}

	return normalizeFieldFilter(filter, context);
}

/**
 * Normalizes `_filter` entries inside relation `deep` query objects while preserving sibling
 * options such as `_limit`, `_offset`, `_sort`, and branches that do not contain filters.
 */
export function normalizeDeepFilterOperandsForBackend(
	deep: Record<string, DeepEntry>,
	context: FilterOperandNormalizationContext,
): Record<string, DeepEntry> {
	const result: Record<string, DeepEntry> = {};

	for (const [relationName, entry] of Object.entries(deep)) {
		result[relationName] = normalizeDeepRelationEntry(relationName, entry, context);
	}

	return result;
}

/**
 * Cheap pre-check used before schema lookup; deep selections that only carry paging, sorting, or
 * nested fields do not need relation metadata for filter normalization.
 */
export function hasDeepFilterEntry(deep: Record<string, DeepEntry>) {
	return Object.values(deep).some((entry) => hasDeepFilterInEntry(entry));
}

function hasDeepFilterInEntry(entry: DeepEntry): boolean {
	if (entry._filter) return true;

	for (const [key, value] of Object.entries(entry)) {
		if (key.startsWith('_')) continue;
		if (isDeepEntry(value) && hasDeepFilterInEntry(value)) return true;
	}

	return false;
}

function normalizeDeepEntry(entry: DeepEntry, context: FilterOperandNormalizationContext): DeepEntry {
	const result: DeepEntry = { ...entry };

	if (entry._filter) {
		result._filter = normalizeFilterOperandsForBackend(collapseOpenRanges(entry._filter), context);
	}

	for (const [relationName, value] of Object.entries(entry)) {
		if (relationName.startsWith('_') || !isDeepEntry(value)) continue;

		result[relationName] = normalizeDeepRelationEntry(relationName, value, context);
	}

	return result;
}

function normalizeDeepRelationEntry(
	relationName: string,
	entry: DeepEntry,
	context: FilterOperandNormalizationContext,
) {
	if (!hasDeepFilterInEntry(entry)) return entry;

	const relationField = context.schemaCollection?.relationFields.find((field) => field.apiName === relationName);

	if (!relationField?.oppositeCollection) {
		throw new Error(`Failed to normalize deep filter: Relation field ${relationName} has no opposite collection`);
	}

	return normalizeDeepEntry(
		entry,
		createRelationNormalizationContext(relationField.oppositeCollection, context),
	);
}

function normalizeFieldFilter(
	filter: FieldFilter,
	context: FilterOperandNormalizationContext,
): FieldFilter {
	const result: FieldFilter = {};

	for (const [fieldName, fieldFilter] of Object.entries(filter)) {
		const primitiveType = context.primitiveFieldTypes?.[fieldName];

		if (primitiveType) {
			result[fieldName] = normalizePrimitiveFieldFilter(fieldFilter, primitiveType);
			continue;
		}

		const relationField = context.schemaCollection?.relationFields.find((field) => field.apiName === fieldName);

		if (relationField) {
			result[fieldName] = normalizeRelationFieldFilter(fieldFilter, relationField, context);
			continue;
		}

		result[fieldName] = fieldFilter;
	}

	return result;
}

function normalizeRelationFieldFilter(
	fieldFilter: FieldFilter[string],
	relationField: ResolvedMonospaceSingleRelationField,
	context: FilterOperandNormalizationContext,
) {
	if (fieldFilter === undefined || fieldFilter === null || typeof fieldFilter !== 'object' || isFilterOperatorObject(fieldFilter)) {
		return fieldFilter;
	}

	if (!relationField.oppositeCollection) {
		throw new Error(`Failed to normalize filter: Relation field ${relationField.apiName} has no opposite collection`);
	}

	const relationContext = createRelationNormalizationContext(relationField.oppositeCollection, context);
	const extracted = extractQuantifier(fieldFilter);

	if (extracted) {
		return {
			[extracted.quantifier]: normalizeFilterOperandsForBackend(extracted.innerFilter, relationContext),
		};
	}

	return normalizeFilterOperandsForBackend(fieldFilter as Filter, relationContext);
}

function createRelationNormalizationContext(
	schemaCollection: ResolvedMonospaceCollection,
	parentContext: FilterOperandNormalizationContext,
): FilterOperandNormalizationContext {
	return {
		primitiveFieldTypes: parentContext.getPrimitiveFieldTypes?.(schemaCollection),
		schemaCollection,
		getPrimitiveFieldTypes: parentContext.getPrimitiveFieldTypes,
	};
}

/**
 * Serializes date-like operands through the field parser contract and rejects non-canonical input.
 * By this point callers should already hold Temporal values, not UI strings or query strings.
 */
function normalizePrimitiveFieldFilter(
	fieldFilter: FieldFilter[string],
	type: PrimitiveType,
) {
	if (!isPrimitiveTypeAnyDateTime(type)) {
		return fieldFilter;
	}

	const typeName = getTypename(type);
	const serializer = resolveFieldParser(typeName)?.serialize;

	const serialize = (value: unknown) => {
		if (value == null) return value;

		assertCanonicalDateLikeOperand(value, typeName);
		return serializer!(value as never);
	};

	if (isFilterOperatorObject(fieldFilter)) {
		const result: Record<string, unknown> = {};

		for (const [operator, value] of Object.entries(fieldFilter)) {
			if (operator === FilterOperator.NULL) {
				result[operator] = value;
				continue;
			}

			result[operator] = Array.isArray(value)
				? value.map((member) => serialize(member))
				: serialize(value);
		}

		return result as FieldFilterOperator;
	}

	return serialize(fieldFilter);
}

function assertCanonicalDateLikeOperand(value: unknown, typeName: PrimitiveTypeName) {
	if (typeName === 'date' && value instanceof Temporal.PlainDate) return;
	if (typeName === 'time' && value instanceof Temporal.PlainTime) return;
	if (typeName === 'dateTime' && value instanceof Temporal.PlainDateTime) return;
	if (typeName === 'dateTimeWithTimezone' && isZonedDateTime(value)) return;

	throw new Error(`Failed to normalize filter: Expected canonical ${typeName} operand`);
}

function isFilterOperatorObject(value: unknown): value is FieldFilterOperator {
	return !!value && typeof value === 'object' && isFieldFilterOperator(value as FieldFilterOperator);
}

function isDeepEntry(value: unknown): value is DeepEntry {
	return !!value && typeof value === 'object' && !Array.isArray(value);
}

function extractQuantifier(value: unknown): ExtractedQuantifier | null {
	if (!value || typeof value !== 'object') return null;

	const innerValue = value as Record<string, unknown>;
	const innerKeys = Object.keys(innerValue);

	if (innerKeys.length !== 1) return null;

	const key = innerKeys[0]!;

	if (!isFilterRelationalQuantifier(key)) return null;

	return {
		quantifier: key,
		innerFilter: innerValue[key] as FieldFilter,
	};
}
